import { useLaunchToast } from '@/composables/useToast';
import type {
    OrderIsEditedNotification,
    SpecialCaseAddedNotification,
    SpecialCaseRemovedNotification,
} from '@/generated';
import type { AutomaticProcessingStatusNotification } from '@/models/automaticProcessingStatus';
import {
    getSignalRConnection,
    hasSignalRReconnectRetryLimitReached,
    resetSignalRReconnectRetryLimitReached,
    startSignalR,
} from '@/services/signalr';
import useAuthStore from '@/stores/useAuthStore';
import useSignalRStore from '@/stores/useSignalRStore';
import { HubConnection } from '@microsoft/signalr';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue';
import { onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const METHOD_NAME = 'receiveNotification';

const signalRReconnectHandlers = new WeakSet<object>();
const signalRFallbackHandlers = new WeakMap<
    HubConnection,
    Map<string, (...args: unknown[]) => void>
>();

const ensureFallbackHandler = (connection: HubConnection, methodName: string) => {
    let methodHandlers = signalRFallbackHandlers.get(connection);
    if (!methodHandlers) {
        methodHandlers = new Map<string, (...args: unknown[]) => void>();
        signalRFallbackHandlers.set(connection, methodHandlers);
    }

    if (methodHandlers.has(methodName)) {
        return;
    }

    const noopHandler = () => {
        // Intentionally empty: keeps SignalR quiet when no page-level listener is mounted.
    };

    methodHandlers.set(methodName, noopHandler);
    connection.on(methodName, noopHandler);
};

export interface NotificationTypeMap {
    OrderIsEditedNotification: OrderIsEditedNotification;
    SpecialCaseAddedNotification: SpecialCaseAddedNotification;
    SpecialCaseRemovedNotification: SpecialCaseRemovedNotification;
    CreditNoteProcessAutomatedStatusNotification: AutomaticProcessingStatusNotification;
}
export interface SignalRNotification {
    notificationType: (keyof NotificationTypeMap)[];
    onNotificationReceived: (arg: unknown) => void;
}

export function useSignalRNotifications({
    notificationType,
    onNotificationReceived,
}: SignalRNotification) {
    const { t } = useI18n();
    const toast = useLaunchToast();
    const { isLiveUpdatesAvailable } = storeToRefs(useSignalRStore());

    const showReconnectInfoToast = toast(
        'warn',
        t('signalR.reconnecting.title'),
        undefined,
        'sticky',
        {
            group: 'signalr',
        }
    );

    const showReconnectFailureToast = toast(
        'error',
        t('signalR.disconnected.title'),
        t('signalR.disconnected.message'),
        'sticky',
        { group: 'signalr' }
    );

    const toasts = useToast();
    const { isAuthenticated } = storeToRefs(useAuthStore());

    let connection: HubConnection | null = null;

    const notificationHandler = <M extends keyof NotificationTypeMap>(
        newMethod: M,
        raw: NotificationTypeMap[M]
    ) => {
        if (!notificationType.includes(newMethod)) {
            return;
        }
        onNotificationReceived(raw);
    };

    const removeNotificationHandler = () => {
        if (!connection) {
            return;
        }
        connection.off(METHOD_NAME, notificationHandler);
    };

    watch(
        isAuthenticated,
        async () => {
            if (!isAuthenticated.value) {
                removeNotificationHandler();
                if (connection && connection.state !== 'Disconnected') {
                    await connection.stop();
                }
                connection = null;
                return;
            }

            try {
                await startSignalR();
                connection = await getSignalRConnection();

                if (connection && !signalRReconnectHandlers.has(connection)) {
                    resetSignalRReconnectRetryLimitReached();
                    toasts.removeGroup('signalr');
                    signalRReconnectHandlers.add(connection);
                    isLiveUpdatesAvailable.value = true;

                    connection.onreconnecting(() => {
                        showReconnectInfoToast();
                        isLiveUpdatesAvailable.value = false;
                    });

                    connection.onreconnected(() => {
                        resetSignalRReconnectRetryLimitReached();
                        toasts.removeGroup('signalr');
                        isLiveUpdatesAvailable.value = true;
                    });

                    connection.onclose(() => {
                        if (!hasSignalRReconnectRetryLimitReached()) {
                            return;
                        }
                        isLiveUpdatesAvailable.value = false;
                        toasts.removeGroup('signalr');
                        showReconnectFailureToast();
                        signalRReconnectHandlers.delete(connection as object);
                    });
                }

                if (connection) {
                    ensureFallbackHandler(connection, METHOD_NAME);
                    connection.off(METHOD_NAME, notificationHandler);
                    connection.on(METHOD_NAME, notificationHandler);
                }
            } catch (error) {
                console.error('Error starting SignalR connection:', error);
            }
        },
        { immediate: true }
    );

    onUnmounted(() => {
        removeNotificationHandler();
    });
}
